import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './glassPatternsDefs';

export function drawHalfEllipseShape(
  svg,
  initX,
  initY,
  w,                     // width of HALF ellipse
  hRect,
  archHeight,
  frameWidth = 10,
  frameColor = 'white',
  lineFrameColor = 'black',
  outerStrokeColor = 'black',
  outerStrokeWidth = 1,
  color = '#EDF9FF',
  includeInnerTopLine = false,
  glassType = null,
  glassDesign = null,
  orientation = 'left',   // NEW: 'left' or 'right'
  gap = 2,
  drawFrame = true        // NEW: whether to draw middle frame
) {
  // Create a group to clip
  const clipId = `clipHalf_${Math.random().toString(36).substr(2, 9)}`;
  const g = svg.append('g');

  // Define clipping rectangle depending on orientation
  const clip = svg.append('clipPath').attr('id', clipId);

  if (orientation === 'left') {
    clip.append('rect')
      .attr('x', initX - frameWidth)
      .attr('y', initY - frameWidth)
      .attr('width', w / 2 + frameWidth * 2)
      .attr('height', archHeight + hRect + frameWidth * 2 + 2);
  } else {
    // RIGHT quarter
    clip.append('rect')
      .attr('x', initX + w / 2 - frameWidth)
      .attr('y', initY - frameWidth)
      .attr('width', w / 2 + frameWidth * 2)
      .attr('height', archHeight + hRect + frameWidth * 2 + 2);
  }

  g.attr('clip-path', `url(#${clipId})`);

  // Draw the full half ellipse inside the clipped group
  const rX = w / 2;
  const rY = archHeight;

  const left = initX;
  const right = initX + w;
  const centerX = initX + rX;
  const centerY = initY + rY;
  const bottom = initY + archHeight + hRect;

  const fw = frameWidth;

  // ====== Glass Fill (Base Layer) ======
  let baseFill = color;
  let tintOverlay = null;

  if (glassType === 'Tinted') {
    baseFill = 'white';
    if (glassDesign?.toLowerCase() === 'grey') tintOverlay = 'rgba(60,60,60,0.2)';
    if (glassDesign?.toLowerCase() === 'bronze') tintOverlay = 'rgba(90,50,0,0.2)';
  }

  if (glassType === 'Textured' && glassDesign) {
    const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
    baseFill = `url(#${patternId})`;
    defineTexturedGlassPatterns(svg, glassDesign);
  }

  // === Outer path (frame) ===
  const pathOuter = `
    M ${left - fw},${bottom + fw}
    L ${left - fw},${centerY}
    A ${rX + fw},${rY + fw} 0 0 1 ${right + fw},${centerY}
    L ${right + fw},${bottom + fw}
    Z
  `;

  g.append('path')
    .attr('d', pathOuter)
    .attr('fill', frameColor)
    .attr('stroke', lineFrameColor)
    .attr('stroke-width', 1);

  // === Inner path ===
  const pathInner = hRect > 0
    ? `
      M ${left},${bottom}
      L ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${bottom}
      Z
    `
    : `
      M ${left},${centerY}
      A ${rX},${rY} 0 0 1 ${right},${centerY}
      L ${right},${centerY}
      L ${left},${centerY}
      Z
    `;

  // Calculate innerPoints from the path
  const allPoints = [];
  const numPoints = 20;

  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const angle = Math.PI * t; // 0 to PI (180 degrees for half ellipse)
    const px = centerX + rX * Math.cos(Math.PI - angle);
    const py = centerY - rY * Math.sin(angle);
    allPoints.push([px, py]);
  }

  if (hRect > 0) {
    allPoints.push([right, bottom]);
    allPoints.push([left, bottom]);
  }

  // Filter points based on orientation
  const innerPoints = [];
  const midX = initX + w / 2;

  for (const point of allPoints) {
    const [px] = point;
    if (orientation === 'left' && px >= initX && px <= midX) {
      innerPoints.push(point);
    } else if (orientation === 'right' && px >= midX && px <= right) {
      innerPoints.push(point);
    }
  }

  g.append('path')
    .attr('d', pathInner)
    .attr('fill', baseFill)
    .attr('stroke', 'none');

  if (tintOverlay) {
    g.append('path')
      .attr('d', pathInner)
      .attr('fill', tintOverlay)
      .attr('stroke', 'none');
  }

  g.append('path')
    .attr('d', pathInner)
    .attr('fill', 'none')
    .attr('stroke', outerStrokeColor)
    .attr('stroke-width', outerStrokeWidth);

  // === Middle Frame (SubFrame) ===
  if (drawFrame && frameWidth > 0) {
    const subInset = frameWidth / 2;
    const rXmid = rX + subInset;
    const rYmid = rY + subInset;

    const pathMiddle = hRect > 0
      ? `
        M ${left - fw + subInset},${bottom + fw - subInset}
        L ${left - fw + subInset},${centerY}
        A ${rXmid},${rYmid} 0 0 1 ${right + fw - subInset},${centerY}
        L ${right + fw - subInset},${bottom + fw - subInset}
        L ${left - fw + subInset},${bottom + fw - subInset}
      `
      : `
        M ${left - fw + subInset},${centerY}
        A ${rXmid},${rYmid} 0 0 1 ${right + fw - subInset},${centerY}
        L ${right + fw - subInset},${bottom + fw - subInset}
        L ${left - fw + subInset},${bottom + fw - subInset}
        L ${left - fw + subInset},${centerY}
      `;

    g.append('path')
      .attr('d', pathMiddle)
      .attr('fill', 'none')
      .attr('stroke', 'black')
      .attr('stroke-width', 0.5);
  }

  // === Vertical divider (frame-colored rectangle) ===
  const barX = initX + w / 2;
  const dx = barX - centerX;
  const term = 1 - (dx * dx) / (rX * rX);

  if (term >= 0) {
    const dy = Math.sqrt(term) * rY;
    const archTopY = centerY - dy;

    let delta = 0;
    if (orientation === 'left') {
        delta = gap + 1;
    }
    else{
        delta = -gap - 1;
    }
    g.append('rect')
      .attr('x', barX - frameWidth / 2 + delta)
      .attr('y', archTopY)
      .attr('width', frameWidth)
      .attr('height', bottom - archTopY)
      .attr('fill', frameColor)
      .attr('stroke', lineFrameColor)
      .attr('stroke-width', 1);

    if (orientation === 'left') {
        g.append('line')
        .attr('x1', barX + frameWidth)
        .attr('y1', archTopY - frameWidth)
        .attr('x2', barX + frameWidth)
        .attr('y2', initY + archHeight + hRect + frameWidth)
        .attr('stroke', 'black')
        .attr('stroke-width', 1);

        g.append('line')
        .attr('x1', barX + frameWidth/2 - gap)
        .attr('y1', archTopY - frameWidth/2)
        .attr('x2', barX + frameWidth/2 - gap/2)
        .attr('y2', initY + archHeight + hRect + frameWidth/2)
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);
    }
    if (orientation === 'right') {
        g.append('line')
        .attr('x1', barX - frameWidth)
        .attr('y1', archTopY - frameWidth)
        .attr('x2', barX - frameWidth)
        .attr('y2', initY + archHeight + hRect + frameWidth)
        .attr('stroke', 'black')
        .attr('stroke-width', 1);

        g.append('line')
        .attr('x1', barX - frameWidth/2 + gap - 1)
        .attr('y1', archTopY - frameWidth/2)
        .attr('x2', barX - frameWidth/2 + gap - 1)
        .attr('y2', initY + archHeight + hRect + frameWidth/2)
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);
    }
  }

  return innerPoints;
}

